import React, { useState, useMemo } from 'react'
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Checkbox,
  ListItemText,
  Tabs,
  Tab,
  SelectChangeEvent,
  Stack,
} from '@mui/material'
import InsightsIcon from '@mui/icons-material/Insights'
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied'
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied'
import PsychologyIcon from '@mui/icons-material/Psychology'
import PieChartIcon from '@mui/icons-material/PieChart'
import CommentIcon from '@mui/icons-material/Comment'
import ReactECharts from 'echarts-for-react'
import { EChartsOption } from 'echarts'
import tiktokDataImport from '../data/tiktokAllComments.json'

// Handle the data structure properly
const tiktokData = tiktokDataImport as any

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

export default function TiktokAnalytics() {
  const [selectedVideos, setSelectedVideos] = useState<string[]>(['sports'])
  const [tabValue, setTabValue] = useState(0)

  // Video thumbnails - using local images
  const videoThumbnails = {
    sports: '/img/osKMZAAwQBLafNkhFSLIAGGZEYArbkAQIeeZIT~tplv-tiktokx-origin.png',
    brand: '/img/oo9C2iwBwDOAkujiPR0ICBA4IlUZ8SI4nf5OLy~tplv-tiktokx-origin.jfif',
    influencer: '/img/osERXeQ0nACdA0DuCgfoFAAkAMIliA0WFkGohE.jfif'
  }

  // Aggregate data based on selected videos
  const currentData = useMemo(() => {
    if (selectedVideos.length === 0) {
      return {
        commentCount: 0,
        summary: {
          sentiment: { positive: 0, negative: 0, neutral: 0 },
          positiveBreakdown: { relevant: 0, attributed: 0, relevantAndAttributed: 0 },
          emotions: {},
          contentTypes: {},
          avgEngagement: 0,
        }
      }
    }

    if (selectedVideos.length === 1) {
      const videoKey = selectedVideos[0]
      const video = tiktokData.metadata?.videos?.[videoKey]
      if (video) {
        return {
          commentCount: video.count,
          summary: {
            ...video.summary,
            positiveBreakdown: video.summary.positiveBreakdown || { relevant: 0, attributed: 0, relevantAndAttributed: 0 }
          }
        }
      }
      return {
        commentCount: 0,
        summary: {
          sentiment: { positive: 0, negative: 0, neutral: 0 },
          positiveBreakdown: { relevant: 0, attributed: 0, relevantAndAttributed: 0 },
          emotions: {},
          contentTypes: {},
          avgEngagement: 0,
        }
      }
    }

    // Aggregate multiple videos
    const aggregated = {
      commentCount: 0,
      summary: {
        sentiment: { positive: 0, negative: 0, neutral: 0 },
        positiveBreakdown: { relevant: 0, attributed: 0, relevantAndAttributed: 0 },
        emotions: {} as Record<string, number>,
        contentTypes: {} as Record<string, number>,
        avgEngagement: 0,
      }
    }

    selectedVideos.forEach(videoKey => {
      const video = tiktokData.metadata?.videos?.[videoKey]
      if (video) {
        aggregated.commentCount += video.count
        aggregated.summary.sentiment.positive += video.summary.sentiment.positive
        aggregated.summary.sentiment.negative += video.summary.sentiment.negative
        aggregated.summary.sentiment.neutral += video.summary.sentiment.neutral

        // Aggregate positive breakdown if available
        if (video.summary.positiveBreakdown) {
          aggregated.summary.positiveBreakdown.relevant += video.summary.positiveBreakdown.relevant || 0
          aggregated.summary.positiveBreakdown.attributed += video.summary.positiveBreakdown.attributed || 0
          aggregated.summary.positiveBreakdown.relevantAndAttributed += video.summary.positiveBreakdown.relevantAndAttributed || 0
        }

        // Aggregate emotions
        Object.entries(video.summary.emotions).forEach(([emotion, count]) => {
          aggregated.summary.emotions[emotion] = (aggregated.summary.emotions[emotion] || 0) + (count as number)
        })

        // Aggregate content types
        Object.entries(video.summary.contentTypes).forEach(([type, count]) => {
          aggregated.summary.contentTypes[type] = (aggregated.summary.contentTypes[type] || 0) + (count as number)
        })
      }
    })

    return aggregated
  }, [selectedVideos])

  // Category labels
  const categoryLabels: Record<string, string> = {
    'substantive': 'Substantive Content',
    'brief': 'Brief Comments',
    'meme': 'Memes/Nonsense',
    'emoji': 'Emoji Only',
    'spam': 'Spam'
  }

  const categoryColors: Record<string, string> = {
    'substantive': '#2196f3',
    'brief': '#4caf50',
    'meme': '#ff9800',
    'emoji': '#ffc107',
    'spam': '#f44336'
  }

  const emotionColors: Record<string, string> = {
    Joy: '#4caf50',
    Trust: '#2196f3',
    Surprise: '#9c27b0',
    Anticipation: '#ff9800',
    Anger: '#f44336',
    Sadness: '#3f51b5',
    Fear: '#795548',
    Disgust: '#607d8b',
  }

  // Categories chart - changes based on single/multi selection
  const categoriesOption: EChartsOption = useMemo(() => {
    if (selectedVideos.length === 1) {
      // Pie chart for single video
      const contentTypes = currentData.summary?.contentTypes || {}
      const totalComments = currentData.commentCount

      return {
        title: {
          text: 'Content Categories',
          left: 'center',
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
          },
        },
        tooltip: {
          trigger: 'item',
          formatter: (params: any) => {
            const percentage = ((params.value / totalComments) * 100).toFixed(1)
            return `${params.name}: ${params.value} (${percentage}%)`
          },
        },
        series: [{
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: true,
            formatter: (params: any) => {
              const percentage = ((params.value / totalComments) * 100).toFixed(0)
              return `${params.name}\n${percentage}%`
            },
            fontSize: 11,
          },
          data: Object.entries(contentTypes).map(([type, count]) => ({
            value: Number(count),
            name: categoryLabels[type] || type,
            itemStyle: { color: categoryColors[type] || '#9e9e9e' }
          })),
        }],
      }
    } else {
      // Clustered bar chart for multiple videos
      // Always show all categories even if 0
      const categories = Object.keys(categoryLabels)
      const videoColors = {
        sports: '#8884d8',
        brand: '#82ca9d',
        influencer: '#ffc658'
      }

      const series = selectedVideos.map((videoKey) => {
        const video = (tiktokData.metadata?.videos as any)?.[videoKey]
        const videoLabel = videoKey === 'sports' ? 'Sports' :
                          videoKey === 'brand' ? 'Brand' :
                          'Influencer'

        return {
          name: videoLabel,
          type: 'bar' as const,
          data: categories.map(cat => {
            const count = video?.summary?.contentTypes?.[cat] || 0
            const total = video?.count || 1
            return parseFloat(((count / total) * 100).toFixed(1))
          }),
          itemStyle: {
            color: videoColors[videoKey as keyof typeof videoColors]
          }
        }
      })

      return {
        title: {
          text: 'Categories Comparison',
          left: 'center',
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
          },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            let result = params[0].name + '<br/>'
            params.forEach((item: any) => {
              result += `${item.marker} ${item.seriesName}: ${item.value}%<br/>`
            })
            return result
          }
        },
        legend: {
          data: series.map(s => s.name),
          bottom: 10,
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '15%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: categories.map(cat => categoryLabels[cat]),
          axisLabel: {
            rotate: 45,
            fontSize: 11,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Percentage',
          axisLabel: {
            formatter: '{value}%',
          },
        },
        series: series,
      }
    }
  }, [selectedVideos, currentData])

  // Sentiment chart - changes to stacked bar for multiple videos
  const sentimentOption: EChartsOption = useMemo(() => {
    if (selectedVideos.length === 1) {
      // Pie chart for single video
      const sentiment = currentData.summary?.sentiment || { positive: 0, negative: 0, neutral: 0 }
      const total = currentData.commentCount

      return {
        title: {
          text: 'Sentiment Distribution',
          left: 'center',
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
          },
        },
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)',
        },
        series: [{
          name: 'Sentiment',
          type: 'pie',
          radius: '65%',
          data: [
            { value: sentiment.positive, name: 'Positive', itemStyle: { color: '#4caf50' } },
            { value: sentiment.neutral, name: 'Neutral', itemStyle: { color: '#ff9800' } },
            { value: sentiment.negative, name: 'Negative', itemStyle: { color: '#f44336' } },
          ],
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }],
      }
    } else {
      // Stacked bar chart for multiple videos
      const videoLabels = selectedVideos.map(key =>
        key === 'sports' ? 'Sports' :
        key === 'brand' ? 'Brand' :
        'Influencer'
      )

      const positiveData = selectedVideos.map(key => {
        const video = (tiktokData.metadata?.videos as any)?.[key]
        if (!video) return 0
        return parseFloat(((video.summary?.sentiment?.positive / video.count) * 100).toFixed(1))
      })

      const neutralData = selectedVideos.map(key => {
        const video = (tiktokData.metadata?.videos as any)?.[key]
        if (!video) return 0
        return parseFloat(((video.summary?.sentiment?.neutral / video.count) * 100).toFixed(1))
      })

      const negativeData = selectedVideos.map(key => {
        const video = (tiktokData.metadata?.videos as any)?.[key]
        if (!video) return 0
        return parseFloat(((video.summary?.sentiment?.negative / video.count) * 100).toFixed(1))
      })

      return {
        title: {
          text: 'Sentiment Comparison',
          left: 'center',
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
          },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            let result = params[0].name + '<br/>'
            params.forEach((item: any) => {
              result += `${item.marker} ${item.seriesName}: ${item.value}%<br/>`
            })
            return result
          }
        },
        legend: {
          data: ['Positive', 'Neutral', 'Negative'],
          bottom: 10,
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '15%',
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          name: 'Percentage',
          axisLabel: {
            formatter: '{value}%',
          },
        },
        yAxis: {
          type: 'category',
          data: videoLabels,
        },
        series: [
          {
            name: 'Positive',
            type: 'bar' as const,
            stack: 'total',
            data: positiveData,
            itemStyle: { color: '#4caf50' },
          },
          {
            name: 'Neutral',
            type: 'bar' as const,
            stack: 'total',
            data: neutralData,
            itemStyle: { color: '#ff9800' },
          },
          {
            name: 'Negative',
            type: 'bar' as const,
            stack: 'total',
            data: negativeData,
            itemStyle: { color: '#f44336' },
          },
        ],
      }
    }
  }, [selectedVideos, currentData])

  // Emotions chart - updated to show % of comments with emotions
  const emotionsOption: EChartsOption = useMemo(() => {
    if (selectedVideos.length === 1) {
      // Bar chart for single video showing % of comments with each emotion
      const emotions = currentData.summary?.emotions || {}
      const totalWithEmotions = Object.values(emotions).reduce((sum: number, count) => sum + (count as number), 0)
      const emotionData = Object.entries(emotions)
        .filter(([_, count]) => (count as number) > 0)
        .map(([name, count]) => ({
          name,
          value: parseFloat(((count as number / totalWithEmotions) * 100).toFixed(1))
        }))
        .sort((a, b) => b.value - a.value)

      return {
        title: {
          text: 'Emotions (% of Emotional Comments)',
          left: 'center',
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
          },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            return `${params[0].name}: ${params[0].value}%`
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '15%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: emotionData.map(d => d.name),
          axisLabel: {
            rotate: 45,
            fontSize: 11,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Percentage',
          axisLabel: {
            formatter: '{value}%',
          },
        },
        series: [{
          type: 'bar' as const,
          data: emotionData.map(d => ({
            value: d.value,
            itemStyle: { color: emotionColors[d.name] || '#999' }
          })),
        }],
      }
    } else {
      // Clustered column chart for multiple videos
      // Always show all emotions even if 0
      const emotionTypes = ['Joy', 'Trust', 'Surprise', 'Anticipation', 'Anger', 'Sadness', 'Fear', 'Disgust']
      const videoColors = {
        sports: '#8884d8',
        brand: '#82ca9d',
        influencer: '#ffc658'
      }

      const series = selectedVideos.map((videoKey) => {
        const video = (tiktokData.metadata?.videos as any)?.[videoKey]
        const videoLabel = videoKey === 'sports' ? 'Sports' :
                          videoKey === 'brand' ? 'Brand' :
                          'Influencer'

        const emotions = video?.summary?.emotions || {}
        const totalWithEmotions = Object.values(emotions).reduce((sum: number, count) => sum + (count as number), 0) || 1

        return {
          name: videoLabel,
          type: 'bar' as const,
          data: emotionTypes.map(emotion => {
            const count = emotions[emotion] || 0
            return totalWithEmotions > 0 ? parseFloat(((count / totalWithEmotions) * 100).toFixed(1)) : 0
          }),
          itemStyle: {
            color: videoColors[videoKey as keyof typeof videoColors]
          }
        }
      })

      return {
        title: {
          text: 'Emotions Comparison (% of Emotional Comments)',
          left: 'center',
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
          },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            let result = params[0].name + '<br/>'
            params.forEach((item: any) => {
              result += `${item.marker} ${item.seriesName}: ${item.value}%<br/>`
            })
            return result
          }
        },
        legend: {
          data: series.map(s => s.name),
          bottom: 10,
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '15%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: emotionTypes,
          axisLabel: {
            rotate: 45,
            fontSize: 11,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Percentage',
          axisLabel: {
            formatter: '{value}%',
          },
        },
        series: series,
      }
    }
  }, [selectedVideos, currentData])

  // Get sentiment percentages for cards
  const getSentimentPercentages = () => {
    const data = currentData.summary?.sentiment || { positive: 0, negative: 0, neutral: 0 }
    const total = currentData.commentCount
    return {
      positive: total > 0 ? ((data.positive / total) * 100).toFixed(1) : '0',
      neutral: total > 0 ? ((data.neutral / total) * 100).toFixed(1) : '0',
      negative: total > 0 ? ((data.negative / total) * 100).toFixed(1) : '0',
    }
  }

  const sentimentPercentages = getSentimentPercentages()

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            TikTok Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tiktokData.metadata.totalComments} comments from 3 TikTok videos
          </Typography>
        </Box>

        {/* Video Thumbnails Selector */}
        <Grid container spacing={2} sx={{ width: 'auto' }}>
          <Grid item>
            <Card
              sx={{
                cursor: 'pointer',
                border: selectedVideos.length === 3 ? '2px solid #9c27b0' : '1px solid #e0e0e0',
                '&:hover': { boxShadow: 3 }
              }}
              onClick={() => {
                // Always select all videos when clicking "All"
                setSelectedVideos(['sports', 'brand', 'influencer'])
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Stack alignItems="center" spacing={1}>
                  <Box sx={{
                    width: 120,
                    height: 160,
                    bgcolor: '#9c27b0',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    <Typography variant="h6" color="white">ALL</Typography>
                    <Typography variant="caption" color="white">VIDEOS</Typography>
                  </Box>
                  <Typography variant="caption">All Videos</Typography>
                  <Typography variant="caption" color="text.secondary">324 comments</Typography>
                  {selectedVideos.length === 3 && (
                    <Chip label="Selected" size="small" color="primary" />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item>
            <Card
              sx={{
                cursor: 'pointer',
                border: selectedVideos.includes('sports') ? '2px solid #9c27b0' : '1px solid #e0e0e0',
                '&:hover': { boxShadow: 3 }
              }}
              onClick={() => {
                if (selectedVideos.includes('sports')) {
                  // Only deselect if there's more than one video selected
                  if (selectedVideos.length > 1) {
                    setSelectedVideos(selectedVideos.filter(v => v !== 'sports'))
                  }
                } else {
                  setSelectedVideos([...selectedVideos, 'sports'])
                }
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Stack alignItems="center" spacing={1}>
                  <Box sx={{
                    width: 120,
                    height: 160,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: '#2196f3',
                    position: 'relative'
                  }}>
                    <img
                      src={videoThumbnails.sports}
                      alt="Sky Sports Football"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none'
                      }}
                    />
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      bgcolor: 'rgba(33, 150, 243, 0.9)',
                      zIndex: -1
                    }}>
                      <Typography variant="h2" color="white">⚽</Typography>
                      <Typography variant="caption" color="white">Sky Sports</Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption">@skysportsfootball</Typography>
                  <Typography variant="caption" color="text.secondary">121 comments</Typography>
                  {selectedVideos.includes('sports') && (
                    <Chip label="Selected" size="small" color="primary" />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item>
            <Card
              sx={{
                cursor: 'pointer',
                border: selectedVideos.includes('brand') ? '2px solid #9c27b0' : '1px solid #e0e0e0',
                '&:hover': { boxShadow: 3 }
              }}
              onClick={() => {
                if (selectedVideos.includes('brand')) {
                  // Only deselect if there's more than one video selected
                  if (selectedVideos.length > 1) {
                    setSelectedVideos(selectedVideos.filter(v => v !== 'brand'))
                  }
                } else {
                  setSelectedVideos([...selectedVideos, 'brand'])
                }
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Stack alignItems="center" spacing={1}>
                  <Box sx={{
                    width: 120,
                    height: 160,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: '#ff9800',
                    position: 'relative'
                  }}>
                    <img
                      src={videoThumbnails.brand}
                      alt="Hello Sky"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none'
                      }}
                    />
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      bgcolor: 'rgba(255, 152, 0, 0.9)',
                      zIndex: -1
                    }}>
                      <Typography variant="h2" color="white">📺</Typography>
                      <Typography variant="caption" color="white">Hello Sky</Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption">@hellosky</Typography>
                  <Typography variant="caption" color="text.secondary">100 comments</Typography>
                  {selectedVideos.includes('brand') && (
                    <Chip label="Selected" size="small" color="primary" />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item>
            <Card
              sx={{
                cursor: 'pointer',
                border: selectedVideos.includes('influencer') ? '2px solid #9c27b0' : '1px solid #e0e0e0',
                '&:hover': { boxShadow: 3 }
              }}
              onClick={() => {
                if (selectedVideos.includes('influencer')) {
                  // Only deselect if there's more than one video selected
                  if (selectedVideos.length > 1) {
                    setSelectedVideos(selectedVideos.filter(v => v !== 'influencer'))
                  }
                } else {
                  setSelectedVideos([...selectedVideos, 'influencer'])
                }
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Stack alignItems="center" spacing={1}>
                  <Box sx={{
                    width: 120,
                    height: 160,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: '#4caf50',
                    position: 'relative'
                  }}>
                    <img
                      src={videoThumbnails.influencer}
                      alt="Cam Fitzzz"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none'
                      }}
                    />
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      bgcolor: 'rgba(76, 175, 80, 0.9)',
                      zIndex: -1
                    }}>
                      <Typography variant="h2" color="white">🎬</Typography>
                      <Typography variant="caption" color="white">Cam Fitzzz</Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption">@camfitzzz</Typography>
                  <Typography variant="caption" color="text.secondary">103 comments</Typography>
                  {selectedVideos.includes('influencer') && (
                    <Chip label="Selected" size="small" color="primary" />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%', minHeight: 160 }}>
            <CardContent>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CommentIcon color="primary" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Comments
                  </Typography>
                </Box>
                <Typography variant="h4">{currentData.commentCount}</Typography>
                <Typography variant="caption" color="text.secondary">
                  From {selectedVideos.length} video{selectedVideos.length !== 1 ? 's' : ''}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#4caf50', color: 'white', height: '100%', minHeight: 160 }}>
            <CardContent>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SentimentSatisfiedIcon />
                  <Typography variant="subtitle2">Positive</Typography>
                </Box>
                <Typography variant="h4">{sentimentPercentages.positive}%</Typography>
                <Typography variant="caption">
                  {currentData.summary?.sentiment?.positive || 0} comments
                </Typography>
                {currentData.summary?.positiveBreakdown && currentData.summary.sentiment.positive > 0 && (
                  <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.3)' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.9 }}>
                      Relevant: {currentData.summary.positiveBreakdown.relevant} |
                      Attributed: {currentData.summary.positiveBreakdown.attributed}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#ff9800', color: 'white', height: '100%', minHeight: 160 }}>
            <CardContent>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PsychologyIcon />
                  <Typography variant="subtitle2">Neutral</Typography>
                </Box>
                <Typography variant="h4">{sentimentPercentages.neutral}%</Typography>
                <Typography variant="caption">
                  {currentData.summary?.sentiment?.neutral || 0} comments
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#f44336', color: 'white', height: '100%', minHeight: 160 }}>
            <CardContent>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SentimentDissatisfiedIcon />
                  <Typography variant="subtitle2">Negative</Typography>
                </Box>
                <Typography variant="h4">{sentimentPercentages.negative}%</Typography>
                <Typography variant="caption">
                  {currentData.summary?.sentiment?.negative || 0} comments
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Paper sx={{ p: 3 }}>
        {/* View Selector */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Select Videos</InputLabel>
              <Select
                multiple
                value={selectedVideos}
                onChange={(e: SelectChangeEvent<typeof selectedVideos>) => {
                  const value = e.target.value
                  const newSelection = typeof value === 'string' ? value.split(',') : value
                  // Ensure at least one video is always selected
                  if (newSelection.length > 0) {
                    setSelectedVideos(newSelection)
                  }
                }}
                label="Select Videos"
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return 'No videos selected'
                  }
                  if (selected.length === 3) {
                    return 'All Videos'
                  }
                  const labels: Record<string, string> = {
                    sports: 'Sports',
                    brand: 'Brand',
                    influencer: 'Influencer'
                  }
                  return selected.map(s => labels[s]).join(', ')
                }}
              >
                <MenuItem value="sports">
                  <Checkbox checked={selectedVideos.indexOf('sports') > -1} />
                  <ListItemText primary="Sports Video" secondary={`${(tiktokData.metadata?.videos as any)?.sports?.count || 0} comments`} />
                </MenuItem>
                <MenuItem value="brand">
                  <Checkbox checked={selectedVideos.indexOf('brand') > -1} />
                  <ListItemText primary="Brand Video" secondary={`${(tiktokData.metadata?.videos as any)?.brand?.count || 0} comments`} />
                </MenuItem>
                <MenuItem value="influencer">
                  <Checkbox checked={selectedVideos.indexOf('influencer') > -1} />
                  <ListItemText primary="Influencer Video" secondary={`${(tiktokData.metadata?.videos as any)?.influencer?.count || 0} comments`} />
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Chip
                label={`${currentData.commentCount} Comments`}
                color="primary"
                icon={<InsightsIcon />}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Charts Tabs */}
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
          <Tab label="Categories" icon={<PieChartIcon />} iconPosition="start" />
          <Tab label="Sentiment" icon={<SentimentSatisfiedIcon />} iconPosition="start" />
          <Tab label="Emotions" icon={<PsychologyIcon />} iconPosition="start" />
        </Tabs>

        {/* Tab Content */}
        <TabPanel value={tabValue} index={0}>
          <Paper sx={{ p: 2, height: 400 }}>
            <ReactECharts
              key={`categories-${selectedVideos.length === 1 ? 'single' : 'multi'}-${selectedVideos.join('-')}`}
              option={categoriesOption}
              notMerge={true}
              lazyUpdate={false}
              style={{ height: '100%', width: '100%' }}
            />
          </Paper>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Stack spacing={3}>
            <Paper sx={{ p: 2, height: 400 }}>
              <ReactECharts
                key={`sentiment-${selectedVideos.length === 1 ? 'single' : 'multi'}-${selectedVideos.join('-')}`}
                option={sentimentOption}
                notMerge={true}
                lazyUpdate={false}
                style={{ height: '100%', width: '100%' }}
              />
            </Paper>

            {/* Positive Sentiment Breakdown for each selected video */}
            {selectedVideos.length > 0 && currentData.summary?.positiveBreakdown && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Positive Sentiment Quality Breakdown
                </Typography>
                <Grid container spacing={2}>
                  {selectedVideos.map(videoKey => {
                    const video = (tiktokData.metadata?.videos as any)?.[videoKey]
                    if (!video?.summary?.positiveBreakdown) return null

                    const breakdown = video.summary.positiveBreakdown
                    const positive = video.summary.sentiment.positive

                    return (
                      <Grid item xs={12} md={selectedVideos.length === 1 ? 12 : 4} key={videoKey}>
                        <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom>
                            {videoKey.charAt(0).toUpperCase() + videoKey.slice(1)} Video
                          </Typography>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Total Positive: {positive} comments
                            </Typography>
                          </Box>
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2">
                                <strong>Relevant to Sky:</strong>
                              </Typography>
                              <Chip
                                label={`${breakdown.relevant} (${((breakdown.relevant/positive)*100).toFixed(0)}%)`}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2">
                                <strong>Attributed to Sky:</strong>
                              </Typography>
                              <Chip
                                label={`${breakdown.attributed} (${((breakdown.attributed/positive)*100).toFixed(0)}%)`}
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                            </Box>
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              pt: 1,
                              borderTop: '1px solid #e0e0e0'
                            }}>
                              <Typography variant="body2">
                                <strong>High-Value (Both):</strong>
                              </Typography>
                              <Chip
                                label={`${breakdown.relevantAndAttributed} (${((breakdown.relevantAndAttributed/positive)*100).toFixed(0)}%)`}
                                size="small"
                                color="secondary"
                              />
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>
                    )
                  })}
                </Grid>
              </Box>
            )}
          </Stack>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Paper sx={{ p: 2, height: 400 }}>
            <ReactECharts
              key={`emotions-${selectedVideos.length === 1 ? 'single' : 'multi'}-${selectedVideos.join('-')}`}
              option={emotionsOption}
              notMerge={true}
              lazyUpdate={false}
              style={{ height: '100%', width: '100%' }}
            />
          </Paper>
        </TabPanel>
      </Paper>

    </Container>
  )
}